import React from 'react';
import CommonPagination from '../../../components/common/CommonPagination';
import PAGINATION_CONFIG from '../../../config/paginationConfig';

export const MasterTable = ({
  columns = [],
  data = [],
  loading = false,
  emptyTitle = 'No records found',
  emptyDescription = 'Try adjusting your search criteria or clearing active filters.',
  emptyMessage,
  // Selection
  selectable = false,
  selectedIds = [],
  selectedRowKeys = [],
  onToggleSelectRow,
  onSelectRows,
  onToggleSelectAll,
  idKey = 'id',
  rowKey,
  // Pagination
  pagination = null, // { page, pageSize, totalCount, total, onPageChange, onPageSizeChange }
  // Row click
  onRowClick = null
}) => {
  // Resolve key
  const getKey = (row, index) => {
    if (typeof rowKey === 'function') return rowKey(row);
    if (typeof rowKey === 'string' && row[rowKey] !== undefined) return row[rowKey];
    if (idKey && row[idKey] !== undefined) return row[idKey];
    return index;
  };

  const activeSelected = selectedIds.length > 0 ? selectedIds : selectedRowKeys;
  const allSelected = selectable && data.length > 0 && data.every((row, idx) => activeSelected.includes(getKey(row, idx)));

  const handleRowToggle = (key) => {
    if (onToggleSelectRow) {
      onToggleSelectRow(key);
    } else if (onSelectRows) {
      if (activeSelected.includes(key)) {
        onSelectRows(activeSelected.filter(k => k !== key));
      } else {
        onSelectRows([...activeSelected, key]);
      }
    }
  };

  const handleAllToggle = (checked) => {
    if (onToggleSelectAll) {
      onToggleSelectAll(checked);
    } else if (onSelectRows) {
      onSelectRows(checked ? data.map((r, i) => getKey(r, i)) : []);
    }
  };

  // Total records count for centralized pagination
  const total = pagination ? (pagination.totalCount ?? pagination.total ?? (Array.isArray(data) ? data.length : 0)) : 0;

  return (
    <div className="mst-table-card">
      <div className="mst-table-viewport">
        <table className="mst-table">
          <thead>
            <tr>
              {selectable && (
                <th style={{ width: '38px', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => handleAllToggle(e.target.checked)}
                    aria-label="Select all rows"
                    style={{ cursor: 'pointer' }}
                  />
                </th>
              )}
              {columns.map((col, idx) => {
                const title = col.header || col.title || '';
                return (
                  <th
                    key={col.key || idx}
                    style={{
                      width: col.width || 'auto',
                      minWidth: col.minWidth || col.width || undefined,
                      textAlign: col.align || 'left'
                    }}
                  >
                    {title}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              // Shimmer Loading Skeleton Rows
              Array.from({ length: 6 }).map((_, idx) => (
                <tr key={idx}>
                  <td colSpan={columns.length + (selectable ? 1 : 0)} style={{ padding: '8px 12px' }}>
                    <div
                      style={{
                        height: '18px',
                        background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
                        backgroundSize: '200% 100%',
                        borderRadius: '4px',
                        animation: 'mst-shimmer 1.4s infinite'
                      }}
                    />
                  </td>
                </tr>
              ))
            ) : data.length === 0 ? (
              // Empty State
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)}>
                  <div style={{ padding: '36px 20px', textAlign: 'center', color: '#64748b' }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" style={{ margin: '0 auto 8px auto', display: 'block' }}>
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
                      {emptyMessage || emptyTitle}
                    </div>
                    <div style={{ fontSize: '11.5px', marginTop: '3px', color: '#64748b' }}>
                      {emptyDescription}
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              // Real Data Rows
              data.map((row, rowIdx) => {
                const k = getKey(row, rowIdx);
                const isSelected = selectable && activeSelected.includes(k);
                const isInactive = row.isActive === false;

                return (
                  <tr
                    key={k}
                    className={`${isInactive ? 'row-inactive' : ''}`}
                    onClick={() => onRowClick && onRowClick(row)}
                    style={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      backgroundColor: isSelected ? '#f0f9ff' : undefined
                    }}
                  >
                    {selectable && (
                      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleRowToggle(k)}
                          aria-label={`Select row ${rowIdx + 1}`}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                    )}
                    {columns.map((col, colIdx) => (
                      <td
                        key={col.key || colIdx}
                        style={{
                          textAlign: col.align || 'left',
                          width: col.width || 'auto',
                          minWidth: col.minWidth || col.width || undefined
                        }}
                      >
                        {col.render ? col.render(row, rowIdx) : row[col.key] ?? '—'}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Standardized LIMS Centralized Pagination */}
      {pagination && (
        <CommonPagination
          page={pagination.page || 1}
          pageSize={pagination.pageSize || PAGINATION_CONFIG.defaultPageSize}
          totalRecords={total}
          onPageChange={pagination.onPageChange}
          onPageSizeChange={pagination.onPageSizeChange}
          loading={loading}
          onExportCurrentPage={pagination.onExportCurrentPage}
          onExportAll={pagination.onExportAll}
        />
      )}
    </div>
  );
};

export default MasterTable;
